import { useQuery } from '@tanstack/react-query';

import { evaluationService } from '@/services/api';

const queryKeys = {
  evaluationAgents: {
    all: ['compass', 'evaluationAgents'] as const,
    byOrganization: (
      organizationId: string,
      callStartTimeAfter?: string,
      callStartTimeBefore?: string,
      ineligible?: boolean,
    ) =>
      [
        ...queryKeys.evaluationAgents.all,
        'byOrganization',
        organizationId,
        callStartTimeAfter ?? '',
        callStartTimeBefore ?? '',
        ineligible ?? false,
      ] as const,
  },
};

interface EvaluationAgentsDateBounds {
  callStartTimeAfter?: string | undefined;
  callStartTimeBefore?: string | undefined;
  // Include agents whose only evaluations in the window are ineligible. Compass
  // excludes ineligible evaluations by default (AQ-834).
  ineligible?: boolean | undefined;
}

export const useEvaluationAgents = (
  organizationId?: string,
  dateBounds?: EvaluationAgentsDateBounds,
) => {
  const callStartTimeAfter = dateBounds?.callStartTimeAfter;
  const callStartTimeBefore = dateBounds?.callStartTimeBefore;
  const ineligible = dateBounds?.ineligible;
  return useQuery({
    queryKey: queryKeys.evaluationAgents.byOrganization(
      organizationId || '',
      callStartTimeAfter,
      callStartTimeBefore,
      ineligible,
    ),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      return evaluationService.getEvaluationAgents({
        organization_id: organizationId,
        call_start_time_after: callStartTimeAfter,
        call_start_time_before: callStartTimeBefore,
        ineligible: ineligible || undefined,
      });
    },
    // Require both date bounds — the agents aggregation is only index-backed when
    // scoped to a call_start_time window, so the UI must never call it unbounded (AQ-517).
    enabled: !!organizationId && !!callStartTimeAfter && !!callStartTimeBefore,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
